import * as _ from 'lodash'

import { Path, PathStep, resolvePathStep } from './Path'
import { any } from 'prop-types';
import { arrRemove } from 'src/util';

interface CursorOptions {
  getter?: (path: Path) => any,
  setter?: (path: Path, value: any) => void,
  updater?: (path: Path, func: (oldValue: any) => any) => void
}

export class CursorBase<Node = any, LastStep extends PathStep = PathStep> {
  protected _path: Path
  protected _options: CursorOptions

  constructor(path: Path, options: CursorOptions = {}) {
    this._path = path
    this._options = options
  }

  path() {
    return this._path
  }

  lastStep() {
    return this._path[this._path.length - 1] as LastStep
  }

  parent() {
    return new Cursor<any>(
      _.initial(this._path),
      this._options
    )
  }

  toJSON() {
    return JSON.stringify(this._path)
  }

  set(newValue: Node) {
    if (!this._options.setter) {
      throw new Error('Cannot call set() on cursor without `setter`')
    }
    this._options.setter(this._path, newValue)
  }

  update(func: (oldValue: Node) => Node) {
    if (!this._options.updater) {
      throw new Error('Cannot call update() on cursor without `updater`')
    }
    this._options.updater(this._path, func)
  }

  delete() {
    this.parent().update((parent) => {
      const key = resolvePathStep(parent, this.lastStep())
      if (parent instanceof Array) {
        return arrRemove(parent, key as number)
      } else {
        return _.omit(parent, key)
      }
    })
  }

  equals(otherCursor: Cursor<Node>) {
    return _.isEqual(this._path, otherCursor._path)
  }
}

export class Cursor<Node, LastStep extends PathStep = PathStep> extends CursorBase<Node, LastStep> {

  constructor(path: Path, options: CursorOptions = {}) {
    super(path, options)
  }

  call<Args extends Array<any>, Return>(func: (cursor: Cursor<Node, LastStep>, ...args: Args) => Return, ...args: Args) {
    return func(this, ...args)
  }

  select<Key extends keyof Node>(key: Key): Cursor<Node[Key], Key> {
    return new Cursor(
      [...this._path, key],
      this._options
    )
  }
  _ = this.select

  select2<Key1 extends keyof Node, Key2 extends keyof Node[Key1]>(key1: Key1, key2: Key2):
      Cursor<Node[Key1][Key2], Key2> {
    return this.select(key1).select(key2)
  }

  select3<Key1 extends keyof Node, Key2 extends keyof Node[Key1], Key3 extends keyof Node[Key1][Key2]>(key1: Key1, key2: Key2, key3: Key3):
      Cursor<Node[Key1][Key2][Key3], Key3> {
    return this.select(key1).select(key2).select(key3)
  }

  static selectPattern<T, Pattern extends _.PartialDeep<T>>(
      cursor: Cursor<Array<T>>,
      pattern: Pattern):
        Cursor<T, Pattern> {
    return new Cursor(
      [...cursor._path, pattern],
      cursor._options
    )
  }

  fetch(): CursorV<Node, LastStep> {
    if (!this._options.getter) {
      throw new Error('Cannot call get() on cursor without `getter`')
    }
    const value = this._options.getter(this._path)
    return new CursorV(
      this._path,
      value,
      this._options
    )
  }

  get(): Node {
    return this.fetch().value()
  }

}

export class CursorV<Node, LastStep extends PathStep = PathStep> extends CursorBase<Node, LastStep> {
  private _value: Node

  constructor(path: Path, value: Node, options: CursorOptions = {}) {
    super(path, options)
    this._value = value
  }

  value() {
    return this._value
  }

  call<Args extends Array<any>, Return>(func: (cursor: CursorV<Node, LastStep>, ...args: Args) => Return, ...args: Args) {
    return func(this, ...args)
  }

  select<Key extends keyof Node>(key: Key): CursorV<Node[Key], Key> {
    return new CursorV(
      [...this._path, key],
      this._value[key],
      this._options
    )
  }
  _ = this.select

  unfetch() {
    return new Cursor<Node, LastStep>(this._path, this._options)
  }

  static selectPattern<T, Pattern extends _.PartialDeep<T>>(
      cursor: CursorV<Array<T>>,
      pattern: Pattern):
        CursorV<T, Pattern> {
    return new CursorV(
      [...cursor._path, pattern],
      findPattern(cursor._value, pattern),
      cursor._options
    )
  }
  static _selectPatternWithProvidedValue<T, Pattern>(
      cursor: CursorV<Array<T>>,
      pattern: Pattern,
      value: T):
        CursorV<T, Pattern> {
    return new CursorV(
      [...cursor._path, pattern],
      value,
      cursor._options
    )
  }

  static elems<Elem>(cursor: CursorV<Array<Elem>>) {
    return _.map(cursor.value(), (_elemValue, i) => {
      return cursor.select(i)
    })
  }
  static elemsKeyed<Elem, KeyProp extends string & keyof Elem>(cursor: CursorV<Array<Elem>>, keyProp: KeyProp){
    return _.map(cursor.value(), (elemValue) => {
      const pattern = {[keyProp]: elemValue[keyProp]} as {[key in KeyProp]: Elem[key]}
      return CursorV._selectPatternWithProvidedValue<Elem, {[key in KeyProp]: Elem[key]}>(cursor, pattern, elemValue)
    })
  }
  static values<Elem>(cursor: CursorV<{[key: string]: Elem}>) {
    return _.map(cursor.value(), (_elemValue, key) => {
      return cursor.select(key)
    })
  }

  static isPresent<T, LastStep>(cursor: CursorV<T | undefined, LastStep>): cursor is CursorV<T, LastStep> {
    return !!cursor.value()
  }

  static unwrapMaybe<T, LastStep>(cursor: CursorV<T | undefined, LastStep>): CursorV<T, LastStep> | undefined {
    return CursorV.isPresent(cursor) ? cursor : undefined
  }
}

function findPattern<T>(array: Array<T>, pattern: _.PartialDeep<T>): T {
  return _.find(array, pattern) as T
}



// WEBPACK FOOTER //
// ./src/lib/Cursor.ts